import { c as _c } from "react-compiler-runtime";
import { MagpieConnection } from 'magpie-lib/connection';
import { getAllowedOrigins } from 'magpie-lib/config';
import { AudienceFilter } from 'magpie-lib/models';
import enviro from 'enviro';
import * as Hublets from 'hubspot-url-utils/hublets-next';
import Raven from 'raven-js';
import { useEffect } from 'react';
import { useEventCallback } from '../hooks/useEventCallback';
import { getHubSpotWindowInfo } from './hubspotWindow';
const getSessionIdFromURL = win => new URLSearchParams(win.location.search).get('sessionId');
export const getMagpieSessionId = win => {
  try {
    const sessionIdFromUrl = getSessionIdFromURL(win);
    if (sessionIdFromUrl) {
      return sessionIdFromUrl;
    }
    const {
      sessionId
    } = getHubSpotWindowInfo(win) || {};
    if (sessionId) {
      return sessionId;
    }
    Raven.captureException(new Error('Unable to get magpie session id'), {
      extra: {
        hubspotWindowInfo: window.hubspot,
        search: win.location.search,
        origin: win.location.origin
      }
    });
    return null;
  } catch (error) {
    Raven.captureException(error, {
      extra: {
        hubspotWindowInfo: window.hubspot,
        search: win.location.search,
        origin: win.location.origin
      }
    });
    return null;
  }
};
export const CollaborationSidebarSessionId = getMagpieSessionId(window);
export const buildAudience = () => {
  const builder = AudienceFilter.builder().crossTab(false).crossPortal(true);
  return builder.build();
};
const getTopic = topic => {
  return MagpieConnection.get().forTopic(topic);
};
export const useMagpieTopic = t0 => {
  "use memo";

  const $ = _c(6);
  const {
    topic,
    message,
    onMessage
  } = t0;
  const skip = !onMessage;
  const stableOnMessage = useEventCallback(onMessage);
  let t1;
  let t2;
  if ($[0] !== message || $[1] !== skip || $[2] !== stableOnMessage || $[3] !== topic) {
    t1 = () => {
      if (skip) {
        return;
      }
      const unsubscribe = getTopic(topic).on(message.getType(), stableOnMessage);
      return () => unsubscribe();
    };
    t2 = [message, skip, stableOnMessage, topic];
    $[0] = message;
    $[1] = skip;
    $[2] = stableOnMessage;
    $[3] = topic;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
};
export const CollaborationSidebarAudience = buildAudience();

// magpie-lib's safe-origins.ts builds preview origins as
// `https://${portalId}.hubspotpreview${isQA ? 'qa' : ''}-${hublet}.com`,
// so a `.hubspotpreview` substring partitions an already-trusted
// `getAllowedOrigins()` list into HubSpot frame vs preview origins
// without us maintaining our own URL parser.
const PREVIEW_HOST_INFIX = '.hubspotpreview';
let cachedAllowedOriginPartition = null;
const getAllowedOriginPartition = () => {
  if (!cachedAllowedOriginPartition) {
    const frame = new Set();
    const preview = new Set();
    for (const origin of getAllowedOrigins()) {
      if (origin.includes(PREVIEW_HOST_INFIX)) {
        preview.add(origin);
      } else {
        frame.add(origin);
      }
    }
    cachedAllowedOriginPartition = {
      frame,
      preview
    };
  }
  return cachedAllowedOriginPartition;
};
export const isCurrentWindowHubspotPreview = () => getAllowedOriginPartition().preview.has(window.location.origin);
export const isHubspotFrameOrigin = origin => getAllowedOriginPartition().frame.has(origin);
export const getHubspotFrameOrigins = () => Array.from(getAllowedOriginPartition().frame);
export const getHubspotPreviewOrigins = () => Array.from(getAllowedOriginPartition().preview);
export const isPortalHubspotPreviewOrigin = origin => getAllowedOriginPartition().preview.has(origin);
const NON_NA1_HUBLETS = Object.values(Hublets).filter(h => {
  var _enviro$ifHublet;
  return (_enviro$ifHublet = enviro.ifHublet({
    isNonNa1: () => true
  }, h)) !== null && _enviro$ifHublet !== void 0 ? _enviro$ifHublet : false;
});
const getCurrentHubletEnv = () => {
  try {
    return {
      hublet: enviro.getHublet(),
      isQa: enviro.isQa()
    };
  } catch (_unused) {
    return null;
  }
};
export const matchesHubletEnv = ({
  origin,
  hublet,
  isQa,
  kind
}) => {
  var _enviro$ifHublet2;
  let host;
  try {
    host = new URL(origin).host;
  } catch (_unused2) {
    return true;
  }
  if (kind === 'preview') {
    const infix = isQa ? 'hubspotpreviewqa' : 'hubspotpreview';
    return host.endsWith(`.${infix}-${hublet}.com`);
  }
  if (host.endsWith('.hsappstatic.net')) {
    return true;
  }
  const envOk = isQa ? host.endsWith('.hubspotqa.com') : host.endsWith('.hubspot.com');
  if (!envOk) {
    return false;
  }
  const sub = host.split('.')[0];
  return (_enviro$ifHublet2 = enviro.ifHublet({
    isNa1: () => !NON_NA1_HUBLETS.some(h => sub.endsWith(`-${h}`)),
    isNonNa1: h => sub.endsWith(`-${h}`)
  }, hublet)) !== null && _enviro$ifHublet2 !== void 0 ? _enviro$ifHublet2 : false;
};
const filterToCurrentHubletEnv = (origins, kind) => {
  const ctx = getCurrentHubletEnv();
  if (!ctx) {
    return origins;
  }
  const filtered = origins.filter(origin => matchesHubletEnv({
    origin,
    hublet: ctx.hublet,
    isQa: ctx.isQa,
    kind
  }));
  return filtered.length > 0 ? filtered : origins;
};
export const getHubspotPreviewOriginsForCurrentEnv = () => filterToCurrentHubletEnv(getHubspotPreviewOrigins(), 'preview');
export const getHubspotFrameOriginsForCurrentEnv = () => filterToCurrentHubletEnv(getHubspotFrameOrigins(), 'frame');